import { Outlet, useLocation } from 'react-router';
import { Moon, Sun } from '@phosphor-icons/react';
import { useTheme } from '~/lib/stores/theme-store';
import { Button } from '~/components/ui/button';
import { BrandBackdrop } from '~/components/brand-backdrop';
import { BrandFooterPill } from '~/components/brand-footer-pill';
import { isTourPhaseSearch } from '~/features/onboard/tour/tour-phase';

/**
 * Split-screen auth shell. Left half: theme-aware brand image with a soft
 * primary-tint wash. Right half: subtle radial gradient so the empty side
 * reads as designed background, not a blank slab.
 *
 * The login card sits centred on the *whole* viewport — floating over the
 * seam where the two halves meet, with a frosted backdrop so both sides
 * bleed through softly behind it.
 *
 * On narrow viewports the image rail collapses and the gradient takes the
 * full width; the card stays centred.
 */
export function AuthLayout() {
  const { theme, toggleTheme } = useTheme();
  const location = useLocation();
  // Onboarding has a multi-step wizard with channel cards & feature toggles
  // that don't fit in the narrow sign-in card width.
  const isOnboard = location.pathname.startsWith('/onboard');
  // FRONT-907: the day-one chat phase (?phase=chat) runs a real chat surface
  // inside this shell — almost full screen, still floating on the backdrop.
  const isChatPhase = isOnboard && new URLSearchParams(location.search).get('phase') === 'chat';
  // FRONT-1014 (Marko, live-test): the guided tour (`?tour=1`) renders itself
  // as a full-viewport `document.body` portal (TourShell) — nothing of its
  // own ever lands in this layout's Outlet slot. Without this check the card
  // below still wraps that empty slot in a bordered/padded box, which sits
  // invisible behind the tour's own opaque overlay right up until the tour's
  // exit-dissolve starts fading that overlay out — at which point the empty
  // card becomes visible, reading as "the tour fades into a blank box"
  // instead of "the tour fades into the next screen".
  //
  // FRONT-1214 (BUG-060): read via the SAME shared helper onboard.tsx uses
  // for its phase derivation — the branch switch below remounts the outlet
  // subtree, and the two readers disagreeing (this one URL-based, that one
  // build-flag-gated state) is exactly what stranded deployed users on a
  // fullscreen wizard.
  const isTourPhase = isOnboard && isTourPhaseSearch(location.search);
  // FRONT-1324: the onboarding wizard's steps are five different heights, and
  // a vertically centred card moves its own top edge every time that height
  // changes — so the progress strip slid out from under the cursor between
  // steps (clicking "People" could land you somewhere else). Anchoring the
  // onboard card near the top pins the strip: the card grows downward, the
  // header and the strip stay put. Everything else (sign-in) keeps the
  // centred float over the image/gradient seam — one card, one height, nothing
  // to shift. The top inset scales with the viewport but never eats more than
  // it has to: it is subtracted from the space `max-h-full` gives the card, and
  // a short window is exactly where the tall step needs every pixel.
  // The day-one chat is deliberately NOT included: it is a fixed-height column
  // (`h-[calc(100vh-8rem)]` in day-one-chat.tsx) sized against the viewport
  // rather than against whatever space this wrapper leaves, so any top inset
  // here pushes its composer past the card's bottom edge on a tall window. It
  // has one height and one step, so it has nothing to keep still anyway.
  const cardAlign = isOnboard && !isChatPhase ? 'items-start px-4 pb-4 pt-[max(1rem,6vh)]' : 'items-center p-4';
  const cardWidth = isChatPhase ? 'max-w-6xl' : isOnboard ? 'max-w-2xl' : 'max-w-sm';
  const cardPadding = isChatPhase ? 'p-4 md:p-6' : 'p-7 md:p-9';

  return (
    <div className="relative min-h-screen overflow-hidden bg-background">
      <BrandBackdrop />

      {/* Theme switcher — anchored top-right of the viewport. Uses the
          shared Button comp so it matches the affordances on /apps. */}
      <div className="absolute top-4 right-4 md:top-6 md:right-6 z-20">
        <Button
          variant="outline"
          size="icon"
          onClick={toggleTheme}
          aria-label={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          tooltip={theme === 'dark' ? 'Switch to light mode' : 'Switch to dark mode'}
          className="bg-card/70 backdrop-blur-md shadow-sm"
        >
          {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
        </Button>
      </div>

      {/* Card — absolutely positioned over the whole viewport so it floats over
          the image/gradient seam. Centred everywhere except the onboarding
          wizard, which is top-anchored so its progress strip can't move
          between steps (see `cardAlign` above). */}
      <div className={`absolute inset-0 z-10 flex justify-center ${cardAlign}`}>
        {isTourPhase ? (
          <Outlet />
        ) : (
          // FRONT-1199: max-h-full + overflow-y-auto so a step taller than
          // the available space (e.g. Calibration's 5 sliders on a phone in
          // portrait) scrolls internally instead of being clipped by the
          // shell's overflow-hidden with no way to reach the rest — a no-op
          // for every step that already fits.
          <div
            data-testid="auth-card"
            className={`w-full ${cardWidth} max-h-full overflow-y-auto rounded-2xl border border-border/60 bg-card/90 backdrop-blur-md
                       shadow-[0_24px_60px_-24px_rgba(0,0,0,0.35)] dark:shadow-[0_24px_60px_-12px_rgba(0,0,0,0.7)]
                       ${cardPadding}`}
          >
            <Outlet />
          </div>
        )}
      </div>

      {/* Brand footer — anchored bottom-centre, frosted pill stays
          readable over the brand-image band. `pointer-events-none` on this
          full-width strip stops it from stealing clicks meant for whatever
          renders underneath it (e.g. a tall wizard step's Finish button,
          FRONT-1135) outside the pill's own visible pixels; the pill
          re-enables pointer events on itself so the link still works.

          FRONT-1324: not on the onboarding routes at all. The pill is anchored
          to the VIEWPORT, not to the card, so on a step tall enough to fill the
          screen it floats over the card's own clipped bottom edge — which made
          truncated content read as a deliberate ending rather than as
          something to scroll. It was already invisible to clicks; the problem
          was purely what it looked like. The wizard carries its own Eddy mark
          in its header, so nothing is lost by dropping it there. */}
      {!isOnboard && (
        <div className="absolute bottom-4 md:bottom-6 inset-x-0 flex items-center justify-center z-10 pointer-events-none">
          <BrandFooterPill />
        </div>
      )}
    </div>
  );
}
